import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ImageOff, Loader2, Trash2, Upload } from "lucide-react";
import toast from "react-hot-toast";
import { uploadBuildingPhoto } from "../utils/buildingPhoto";

const MAX_FILE_MB = 10;

/**
 * Compact primary-photo widget for the office building detail page.
 * Renders the current photo (or a placeholder) with an upload button
 * that replaces it. Removing clears `primaryPhotoUrl` on the form only
 * — nothing is deleted from Storage or the `buildingPhotos` collection,
 * because those records are shared with Raven's building photo gallery
 * and its ops flow may still want the photo around.
 *
 * Notes:
 *   - The public site reads `primaryPhotoUrl` verbatim; nothing else
 *     needs to change when this URL updates.
 *   - `onChange` is fired with the new URL (or "") so the parent form
 *     tracks dirty state and persists on Save.
 *   - Uploads land in the same `buildingPhotos` Firestore collection
 *     and Storage path that Raven uses, so cross-app uploads are
 *     visible in both places.
 */
export default function BuildingPhotoUpload({
  buildingId,
  value,
  onChange,
}) {
  const currentUser = useSelector((state) => state.userObject);
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFiles = async (fileList) => {
    const file = Array.from(fileList || []).find((f) =>
      f.type.startsWith("image/"),
    );
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File exceeds ${MAX_FILE_MB} MB.`);
      return;
    }
    if (!buildingId) {
      toast.error("Building must be saved before uploading photos.");
      return;
    }

    setIsUploading(true);
    setProgress(0);
    try {
      const { url } = await uploadBuildingPhoto({
        buildingId,
        file,
        currentUser,
        onProgress: (p) => setProgress(p),
      });
      onChange?.(url);
      toast.success("Photo uploaded. Remember to Save.");
    } catch (err) {
      console.error("[BuildingPhotoUpload] upload failed:", err);
      toast.error("Upload failed.");
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const handleRemove = () => {
    onChange?.("");
    toast.success("Photo cleared. Remember to Save.");
  };

  return (
    <div>
      <div className="relative aspect-[4/3] bg-slate-100">
        {value ? (
          <img
            src={value}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-400">
            <ImageOff className="w-10 h-10" />
            <span className="text-[11px]">No primary photo set</span>
          </div>
        )}
        {isUploading ? (
          <div className="absolute inset-0 bg-black/50 text-white flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-xs font-medium">
              Uploading… {Math.round(progress * 100)}%
            </span>
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2 p-3 border-t border-gray-100 bg-gray-50/60">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-leechiuOrange text-white text-xs font-medium hover:opacity-90 disabled:opacity-60"
        >
          <Upload className="w-3.5 h-3.5" />
          {value ? "Change photo" : "Upload photo"}
        </button>
        {value ? (
          <button
            type="button"
            onClick={handleRemove}
            disabled={isUploading}
            aria-label="Remove primary photo"
            className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 text-xs font-medium disabled:opacity-60"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>
      <div className="px-3 pb-3 text-[10px] text-gray-400">
        PNG, JPG, WebP — up to {MAX_FILE_MB} MB. Shared with Raven
        (buildingPhotos collection + Storage).
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
